import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Tab,
  Tabs,
} from "@mui/material";
import color from "../../../Constants/colors";
import { DealData, InternalDeal } from "../../../Models/ambassador";
import { useCorrectDeal } from "../../../Query/useAmbassador";
import {
  ClassificationStep,
  CustomerStep,
  ProjectStep,
} from "../Deals/DealForm/DealFormSteps";
import {
  DEAL_FIELDS,
  DealErrors,
  STEP_KEYS,
  toFormData,
  validateStep,
} from "../Deals/DealForm/dealFormModel";

interface Props {
  deal: InternalDeal;
  onClose: () => void;
}

/** como's correction of the submitted data, in the steps of the deal form.
 * Every change is kept in the history with its old and new value. */
const CorrectionDialog: React.FC<Props> = ({ deal, onClose }) => {
  const { t } = useTranslation("ambassador");
  const correctDeal = useCorrectDeal();
  const [data, setData] = useState<DealData>(() => toFormData(deal));
  const [step, setStep] = useState(0);
  const [errors, setErrors] = useState<DealErrors>({});
  const original = toFormData(deal);

  const change = (patch: Partial<DealData>) =>
    setData((current) => ({ ...current, ...patch }));

  const changed = DEAL_FIELDS.filter(
    (field) =>
      field !== "data_protection_confirmed" &&
      JSON.stringify(data[field]) !== JSON.stringify(original[field]),
  );

  const save = () => {
    for (let index = 0; index < 3; index += 1) {
      const stepErrors = validateStep(data, index);
      if (Object.keys(stepErrors).length) {
        setErrors(stepErrors);
        setStep(index);
        return;
      }
    }
    const patch = changed.reduce(
      (values, field) => ({ ...values, [field]: data[field] }),
      {},
    );
    correctDeal.mutate(
      { dealId: deal.id, data: patch },
      { onSuccess: onClose },
    );
  };

  const stepProps = { data, errors, onChange: change };

  return (
    <Dialog open onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ color: color.gray900, fontWeight: 700 }}>
        {t("review.correctionTitle")}
      </DialogTitle>
      <DialogContent>
        <Alert severity="info" sx={{ mb: 2 }}>
          {t("review.correctionHint")}
        </Alert>
        <Tabs
          value={step}
          onChange={(_, value: number) => setStep(value)}
          sx={{ mb: 2, borderBottom: `1px solid ${color.gray200}` }}
          variant="scrollable"
        >
          {STEP_KEYS.slice(0, 3).map((key, index) => (
            <Tab key={key} value={index} label={t(`wizard.steps.${key}`)} />
          ))}
        </Tabs>
        {step === 0 && <CustomerStep {...stepProps} />}
        {step === 1 && <ProjectStep {...stepProps} />}
        {step === 2 && <ClassificationStep {...stepProps} />}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button color="secondary" onClick={onClose}>
          {t("common.cancel")}
        </Button>
        <Button
          variant="contained"
          disabled={!changed.length || correctDeal.isPending}
          onClick={save}
        >
          {t("review.saveCorrection", { count: changed.length })}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CorrectionDialog;
